import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import {
    CoordenadorResponse,
    CctHistoricoResponse,
    CctMembroHistoricoStatusResponse,
    CctMembroRequest,
    CctMembroResponse,
    CctParticipacaoResponse,
    EstruturaRequest,
    EstruturaResponse,
    EstruturaStatusRequest,
    PageEstrutura
} from './comissao-cadastro.model';

@Injectable({ providedIn: 'root' })
export class ComissaoCadastroService extends BaseService {
    private readonly BASE = environment.basePath + '/comissoes/cadastros';

    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    pesquisar(params: { q?: string; status?: string; idTipoComissaoCt?: number | null; idEoSuperior?: number | null; page?: number; size?: number; sort?: string }): Observable<PageEstrutura> {
        let p = new HttpParams().set('page', params.page ?? 0).set('size', params.size ?? 20);
        if (params.q) p = p.set('q', params.q);
        if (params.status) p = p.set('status', params.status);
        if (params.idTipoComissaoCt) p = p.set('idTipoComissaoCt', params.idTipoComissaoCt);
        if (params.idEoSuperior) p = p.set('idEoSuperior', params.idEoSuperior);
        if (params.sort) p = p.set('sort', params.sort);
        return this.http.get<PageEstrutura>(`${this.BASE}/pesquisar`, { params: p });
    }

    buscarPorId(id: number): Observable<EstruturaResponse> {
        return this.http.get<EstruturaResponse>(`${this.BASE}/${id}`);
    }

    salvar(request: EstruturaRequest): Observable<EstruturaResponse> {
        return this.http.post<EstruturaResponse>(this.BASE, request);
    }

    atualizar(id: number, request: EstruturaRequest): Observable<EstruturaResponse> {
        return this.http.put<EstruturaResponse>(`${this.BASE}/${id}`, request);
    }

    toggleStatus(id: number, request?: EstruturaStatusRequest): Observable<EstruturaResponse> {
        return this.http.patch<EstruturaResponse>(`${this.BASE}/${id}/status`, request ?? {});
    }

    excluir(id: number): Observable<void> {
        return this.http.delete<void>(`${this.BASE}/${id}`);
    }

    buscarCoordenador(q: string, page = 0, size = 10): Observable<{ content: CoordenadorResponse[]; totalElements: number }> {
        const params = new HttpParams().set('q', q).set('page', page).set('size', size);
        return this.http.get<{ content: CoordenadorResponse[]; totalElements: number }>(`${this.BASE}/coordenadores`, { params });
    }

    listarParticipacoes(): Observable<CctParticipacaoResponse[]> {
        return this.http.get<CctParticipacaoResponse[]>(`${this.BASE}/participacoes`);
    }

    listarMembrosDetailed(idEstrutura: number): Observable<CctMembroResponse[]> {
        return this.http.get<CctMembroResponse[]>(`${this.BASE}/${idEstrutura}/membros`);
    }

    listarMembrosAtuais(idEstrutura: number): Observable<CctMembroResponse[]> {
        return this.http.get<CctMembroResponse[]>(`${this.BASE}/${idEstrutura}/membros/atuais`);
    }

    listarMembrosAntigos(idEstrutura: number): Observable<CctMembroResponse[]> {
        return this.http.get<CctMembroResponse[]>(`${this.BASE}/${idEstrutura}/membros/antigos`);
    }

    listarHistoricoStatusMembro(idEstrutura: number, idSolicitante: number, uf: string): Observable<CctMembroHistoricoStatusResponse[]> {
        return this.http.get<CctMembroHistoricoStatusResponse[]>(`${this.BASE}/${idEstrutura}/membros/${idSolicitante}/${uf}/historico-status`);
    }

    listarHistoricoCct(idEstrutura: number): Observable<CctHistoricoResponse[]> {
        return this.http.get<CctHistoricoResponse[]>(`${this.BASE}/${idEstrutura}/historico`);
    }

    vincularMembroCct(idEstrutura: number, request: CctMembroRequest): Observable<void> {
        return this.http.post<void>(`${this.BASE}/${idEstrutura}/membros`, request);
    }

    encerrarMembroCct(idEstrutura: number, idSolicitante: number, uf: string, dtFim?: string | null, nuPortariaRemocao?: string | null): Observable<void> {
        let params = new HttpParams();
        if (dtFim) params = params.set('dtFim', dtFim);
        if (nuPortariaRemocao) params = params.set('nuPortariaRemocao', nuPortariaRemocao);
        return this.http.delete<void>(`${this.BASE}/${idEstrutura}/membros/${idSolicitante}/${uf}`, { params });
    }

    registrarAfastamento(idEstrutura: number, idSolicitante: number, uf: string, dataStatus?: string): Observable<void> {
        let params = new HttpParams();
        if (dataStatus) {
            params = params.set('dataStatus', dataStatus);
        }
        return this.http.post<void>(`${this.BASE}/${idEstrutura}/membros/${idSolicitante}/${uf}/afastamento`, {}, { params });
    }
}
